import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { SxProps } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { formatPriceWithDelimiter } from "functions/helper";
import moment from "moment";
import React from "react";
import { BundleBalanceProps } from "_interfaces/bundle-balance";

interface Props {
  data: BundleBalanceProps["countersSummary"];
}

const BalanceCard: React.FC<Props> = ({ data }) => {
  const counterName = data?.counterName || "";

  const isDataCounter =
    counterName === "DATA" || counterName === "CUSTOM DATA NONEXPIRY";

  const isMinuteCounter =
    counterName === "Onnet Mins" ||
    counterName === "CUSTOM OFF-NET MINUTES NONEXPIRY";

  const formatDataValue = (
    value: string | number | null | undefined
  ): string => {
    const numValue = Number(value);
    if (!value || isNaN(numValue) || numValue <= 0) return "0";

    let finalValue = numValue;
    let unit = "MB";

    if (numValue < 1) {
      finalValue = numValue * 1024;
      unit = "KB";
    } else if (numValue >= 1024) {
      finalValue = numValue / 1024;
      unit = "GB";
    }

    return `${formatPriceWithDelimiter(finalValue, false)} ${unit}`;
  };

  const formatDefaultValue = (
    value: string | number | null | undefined
  ): string => {
    const numValue = Number(value);
    if (!value || isNaN(numValue)) return "0";

    const formatted = isMinuteCounter
      ? `${Math.floor(numValue)} Minute`
      : formatPriceWithDelimiter(numValue, false);

    return formatted;
  };

  return (
    <Box
      sx={{
        boxShadow: "0 .25rem 1rem rgba(0,0,0,.08)",
        borderRadius: 3,
        padding: 3,
      }}
    >
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <RenderRow
            label="Counter Name"
            value={`${data?.counterName}`}
            valueSx={{ fontWeight: 600 }}
          />
        </Grid>
        <Grid item xs={6}>
          <RenderRow
            label="Actual Value"
            value={
              isDataCounter
                ? formatDataValue(data?.actualValue)
                : formatDefaultValue(data?.actualValue)
            }
          />
        </Grid>
        <Grid item xs={6}>
          <RenderRow
            label="Used Value"
            value={
              isDataCounter
                ? formatDataValue(data?.usedValue)
                : formatDefaultValue(data?.usedValue)
            }
          />
        </Grid>
        <Grid item xs={6}>
          <RenderRow
            label="Expiry Date"
            value={`${moment(data?.expiryDate).format("Do MMM YYYY")}`}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default BalanceCard;

interface RowProps {
  label: string;
  value: string | number;
  valueSx?: SxProps;
}

const RenderRow: React.FC<RowProps> = ({ label, value, valueSx }) => {
  return (
    <Box>
      <Typography fontSize={12} className="fc-grey">
        {label}
      </Typography>
      <Typography sx={valueSx}>{value}</Typography>
    </Box>
  );
};
